import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import {
  AlternateEmail,
  ArrowBackRounded,
  CheckCircleOutlineRounded,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import AuthLayout, { authStyles as styles } from "../../components/customComponents/authLayout/AuthLayout";
import OtpBoxInput from "../../components/customComponents/otpInput/OtpBoxInput";
import { authApi } from "../../auth/authApi";
import { authErrorMessage, isValidEmail, maskEmail, validatePassword } from "../../utils/authSession";
import { emailDeliveryFailure } from "../../utils/emailDeliveryResponse";

const OTP_LENGTH = 6;
const FALLBACK_RESEND_SECONDS = 60;

/**
 * Forgot-password journey for the email+password flow: email -> OTP -> new password.
 *
 * Unlike the OTP *login* flow this does not auto-submit when the sixth digit lands: verifying
 * here only exchanges the code for a short-lived reset token, and a mistyped digit burning one of
 * a limited number of allowed attempts without the user pressing anything would feel broken. The
 * user confirms.
 *
 * A completed reset is expected to revoke every existing session for that user server-side, so it
 * lands on the sign-in page rather than on /dashboard.
 */
export default function LoginForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState("email"); // "email" | "otp" | "password" | "done"
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const handleRequestOtp = async (event) => {
    if (event) event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const res = await authApi.forgotPassword({ email: normalizedEmail });
      const data = res?.data || {};
      const deliveryError = emailDeliveryFailure(data);
      if (deliveryError) { setError(deliveryError); return; }
      setMaskedEmail(data.maskedEmail || maskEmail(normalizedEmail));
      setCooldown(Number(data.resendAfterSeconds) || FALLBACK_RESEND_SECONDS);
      setNotice(data.message || "We've emailed you a 6-digit code.");
      setOtp("");
      setStep("otp");
    } catch (err) {
      console.error("Forgot-password OTP request failed:", err);
      setError(authErrorMessage(err, "We couldn't send a code to that address."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) return;

    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const res = await authApi.verifyForgotPasswordOtp({ email: email.trim().toLowerCase(), otp });
      const token = res?.data?.resetToken;
      if (!token) {
        setError("The server didn't return a reset token. Please start again.");
        return;
      }
      setResetToken(token);
      setStep("password");
    } catch (err) {
      console.error("Forgot-password OTP verification failed:", err);
      setError(authErrorMessage(err, "That code could not be verified."));
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    const problem = validatePassword(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      // confirmPassword goes to the server too - it re-checks the match rather than trusting this
      // page's comparison.
      await authApi.resetPassword({ resetToken, password, confirmPassword });
      setStep("done");
    } catch (err) {
      console.error("Password reset failed:", err);
      setError(authErrorMessage(err, "We couldn't reset your password."));
    } finally {
      setSubmitting(false);
    }
  };

  const stepCopy = {
    email: {
      heading: "Reset your password",
      subheading: "Enter your account email and we'll send you a 6-digit code.",
      hint: "Step 1 of 3",
    },
    otp: {
      heading: "Check your email",
      subheading: undefined,
      hint: "Step 2 of 3",
    },
    password: {
      heading: "Choose a new password",
      subheading: "At least 8 characters, with one letter and one digit.",
      hint: "Step 3 of 3",
    },
    done: {
      heading: "Password updated",
      subheading: undefined,
      hint: undefined,
    },
  }[step];

  return (
    <AuthLayout
      heading={stepCopy.heading}
      subheading={stepCopy.subheading}
      footer={
        step === "done" ? null : (
          <div className={styles.footerRow}>
            <button type="button" className={styles.linkButton} onClick={() => navigate("/login/password")}>
              <ArrowBackRounded fontSize="inherit" /> Back to sign in
            </button>
          </div>
        )
      }
    >
      {stepCopy.hint && <p className={styles.stepHint}>{stepCopy.hint}</p>}

      {step === "email" && (
        <form className={styles.form} onSubmit={handleRequestOtp}>
          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="forgot-email">
              Email
            </label>
            <input
              id="forgot-email"
              type="email"
              placeholder="Email address"
              className={styles.inputField}
              value={email}
              onChange={(e) => {
                setError("");
                setEmail(e.target.value.trim());
              }}
              autoComplete="email"
              disabled={submitting}
              autoFocus
            />
            <AlternateEmail className={styles.trailingIcon} />
          </div>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button type="submit" className={styles.primaryButton} disabled={!email || submitting}>
            {submitting ? "Sending…" : "Send code"}
          </button>
        </form>
      )}

      {step === "otp" && (
        <form className={styles.form} onSubmit={handleVerifyOtp}>
          <p className={styles.otpMeta}>
            Enter the {OTP_LENGTH}-digit code sent to <strong>{maskedEmail}</strong>
          </p>

          <OtpBoxInput
            value={otp}
            onChange={(next) => {
              setError("");
              setOtp(next);
            }}
            numInputs={OTP_LENGTH}
            disabled={submitting}
          />

          <div className={styles.resendRow}>
            <span>Didn't get the code?</span>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => handleRequestOtp()}
              disabled={cooldown > 0 || submitting}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>

          {notice && !error && (
            <Alert severity="info" className={styles.alert}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={otp.length !== OTP_LENGTH || submitting}
          >
            {submitting ? "Verifying…" : "Verify code"}
          </button>

          <div className={styles.centeredRow}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                setStep("email");
                setOtp("");
                setCooldown(0);
                setError("");
                setNotice("");
              }}
            >
              Use a different email
            </button>
          </div>
        </form>
      )}

      {step === "password" && (
        <form className={styles.form} onSubmit={handleResetPassword}>
          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="forgot-new-password">
              New password
            </label>
            <input
              id="forgot-new-password"
              type={showPassword ? "text" : "password"}
              placeholder="New password"
              className={styles.inputField}
              value={password}
              onChange={(e) => {
                setError("");
                setPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
              autoFocus
            />
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowPassword((prev) => !prev)}
              onMouseDown={(e) => e.preventDefault()}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.visuallyHidden} htmlFor="forgot-confirm-password">
              Confirm new password
            </label>
            <input
              id="forgot-confirm-password"
              type={showPassword ? "text" : "password"}
              placeholder="Confirm new password"
              className={styles.inputField}
              value={confirmPassword}
              onChange={(e) => {
                setError("");
                setConfirmPassword(e.target.value);
              }}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>

          {error && (
            <Alert severity="error" className={styles.alert}>
              {error}
            </Alert>
          )}

          <button
            type="submit"
            className={styles.primaryButton}
            disabled={!password || !confirmPassword || submitting}
          >
            {submitting ? "Saving…" : "Set new password"}
          </button>
        </form>
      )}

      {step === "done" && (
        <div className={styles.form}>
          <Alert severity="success" icon={<CheckCircleOutlineRounded fontSize="small" />} className={styles.alert}>
            Your password has been updated. For your security every device that was signed in has
            been signed out.
          </Alert>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => navigate("/login/password", { replace: true })}
          >
            Sign in
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
